import { AHIcon } from "arkh";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import Animated, {
  runOnJS,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { ScrollView } from "react-native";
import { Section } from "../State/Routine/types";
import { Gesture, GestureDetector } from "react-native-gesture-handler";

import { useListContentStyle } from "../../../components/mobile/useListContentStyle";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../State/RoutineEditState";
import { BasicSectionListItem } from "../TaskScreenComponent/SectionSelectItem";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { useIntl } from "react-intl";
import { RoutineEditScreenProps } from "../RoutineEditMain/RoutineEditNavigation";

export function RoutineEditChecklistOrderSections(
  props: RoutineEditScreenProps<"RoutineEditChecklistOrderSections">
) {
  const { sections = [] } = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();
  const positions = useSharedValue<Positions>(
    Object.assign(
      {},
      ...sections.map((section) => ({ [section.id]: section.ordinal }))
    )
  );
  const [, setS] = useState(0);
  useLayoutEffect(() => {
    const newPositions: Positions = Object.assign(
      {},
      ...sections.map((section) => ({ [section.id]: section.ordinal }))
    );
    positions.value = newPositions;
    setS((f) => f + 1);
  }, [positions, sections]);
  const setOrder = useCallback(
    (val: Positions) => {
      setParams({
        sections: sections.map((s) => ({ ...s, ordinal: val[s.id] })),
      });
    },
    [sections, setParams]
  );
  const height = useDerivedValue(() => {
    return Object.keys(positions.value).length * LIST_ITEM_HEIGHT;
  });
  const listStyle = useAnimatedStyle(() => ({
    height: withTiming(height.value),
  }));
  const contentContainerStyle = useListContentStyle();

  const intl = useIntl();
  useNavigationHeader({
    type: "DONE",
    title: intl.formatMessage({
      defaultMessage: "Reorder sections",
      description: "RoutineEditChecklistOrderSections title",
    }),
    defaultContentStyle: true,
    onDone: () => {
      props.navigation.goBack();
    },
  });

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
    >
      <Animated.View style={listStyle}>
        {sections
          .filter((section) => positions.value[section.id] !== undefined)
          .map((section) => (
            <SectionListItem
              key={section.id}
              onReorder={(pos) => {
                setOrder(pos);
              }}
              positions={positions}
              isSelected
              section={section}
            />
          ))}
      </Animated.View>
    </ScrollView>
  );
}

const LIST_ITEM_HEIGHT = 70;
function getY(p: Positions, id: string) {
  "worklet";
  return p[id] * LIST_ITEM_HEIGHT;
}

const getOrder = (ty: number) => {
  "worklet";
  const y = Math.round(ty / LIST_ITEM_HEIGHT) * LIST_ITEM_HEIGHT;
  const row = Math.max(y, 0) / LIST_ITEM_HEIGHT;
  return row;
};
export type Positions = { [id: string]: number };

function SectionListItem(props: {
  positions: SharedValue<Positions>;
  isSelected: boolean;
  section: Section;
  onReorder: (val: Positions) => void;
}) {
  const positionY = getY(props.positions.value, props.section.id);
  const y = useSharedValue(positionY);
  const isActive = useSharedValue(false);

  useAnimatedReaction(
    () => props.positions.value[props.section.id]!,
    (newOrder) => {
      if (!isActive.value) {
        const pos = newOrder * LIST_ITEM_HEIGHT;
        y.value = withTiming(pos);
      }
    }
  );

  const ctx = useSharedValue({ y: 0 });
  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .onStart(() => {
          ctx.value.y = getY(props.positions.value, props.section.id);
          y.value = ctx.value.y;
          isActive.value = true;
        })
        .onChange(({ translationY }) => {
          y.value = translationY + ctx.value.y;
          const newOrder = getOrder(y.value);
          const oldOrder = props.positions.value[props.section.id];
          if (newOrder !== oldOrder) {
            const idToSwap = Object.keys(props.positions.value).find(
              (key) => props.positions.value[key] === newOrder
            );
            if (idToSwap) {
              const newPositions = Object.assign({}, props.positions.value);
              newPositions[props.section.id] = newOrder;
              newPositions[idToSwap] = oldOrder;
              props.positions.value = newPositions;
            }
          }
        })
        .onEnd(() => {
          const newPosition = props.positions.value[props.section.id];
          y.value = withTiming(
            newPosition * LIST_ITEM_HEIGHT,
            undefined,
            () => {
              isActive.value = false;
              runOnJS(props.onReorder)(props.positions.value);
            }
          );
        })
        .hitSlop({ top: 20, bottom: 20, left: 90, right: 10 })
        .activeOffsetY(0)
        .minDistance(0),
    [ctx.value, isActive, props.onReorder, props.positions, props.section.id, y]
  );

  const scale = useDerivedValue(() => (isActive.value ? 1.03 : 1));

  const style = useAnimatedStyle(
    () => ({
      transform: [{ translateY: y.value }, { scale: withTiming(scale.value) }],
      height: LIST_ITEM_HEIGHT,
      justifyContent: "center",
      zIndex: isActive.value ? 10 : 2,
      position: "absolute",
      left: 0,
      width: "100%",
    }),
    [y, isActive.value]
  );

  return (
    <Animated.View style={style}>
      <GestureDetector gesture={gesture}>
        <BasicSectionListItem
          isFree
          disabled
          section={props.section}
          right={<AHIcon type="drag" />}
        />
      </GestureDetector>
    </Animated.View>
  );
}
